import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { saveSession, homeForRole } from '../lib/auth';

export default function Verify2FA() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  const userId = sessionStorage.getItem('pending_2fa_user_id');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!userId) {
      setError('Session expired. Please log in again.');
      navigate('/login');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const res = await apiFetch('/api/auth/verify-2fa', {
        method: 'POST',
        body: { user_id: userId, code: code.trim() },
      });

      if (res.token) {
        const email = sessionStorage.getItem('pending_2fa_email');
        sessionStorage.removeItem('pending_2fa_user_id');
        sessionStorage.removeItem('pending_2fa_email');
        if (email && res.device_token) {
          localStorage.setItem('mfai_device_' + email, JSON.stringify({
            token: res.device_token,
            expires: Date.now() + 7 * 24 * 60 * 60 * 1000,
          }));
        }
        saveSession(res.token, res.user);
        navigate(homeForRole(res.user?.role));
        return;
      }

      setError(res.message || 'Invalid code. Please try again.');
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!userId) return;
    try {
      await apiFetch('/api/auth/resend-2fa', {
        method: 'POST',
        body: { user_id: userId },
      });
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch {
      setError('Could not resend code. Please try again.');
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>M</div>
          <span style={styles.logoText}>MasterFileAI</span>
        </div>

        <div style={styles.iconWrap}>📧</div>
        <h1 style={styles.heading}>Check your email</h1>
        <p style={styles.sub}>
          We sent a 6-digit verification code to your email address. It expires in 10 minutes.
        </p>

        {error && <div style={styles.errorBox}>{error}</div>}
        {resent && <div style={styles.successBox}>✓ New code sent to your email</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Verification code</label>
          <input
            style={styles.codeInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />

          <button type="submit" style={styles.btn} disabled={loading || code.length !== 6}>
            {loading ? 'Verifying…' : 'Verify code'}
          </button>
        </form>

        <p style={styles.footerText}>
          Didn't receive it?{' '}
          <button onClick={handleResend} style={styles.linkBtn}>Resend code</button>
          {' · '}
          <button onClick={() => navigate('/login')} style={styles.linkBtn}>Back to login</button>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8f9fb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e2e6ef',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
    textAlign: 'center',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 28,
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 18,
  },
  logoText: {
    color: '#111827',
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.3px',
  },
  iconWrap: {
    fontSize: 40,
    marginBottom: 12,
  },
  heading: {
    color: '#111827',
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 10px',
    letterSpacing: '-0.3px',
  },
  sub: {
    color: '#8b8fa8',
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  errorBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.4)',
    borderRadius: 8,
    color: '#f87171',
    fontSize: 13,
    padding: '10px 14px',
    marginBottom: 16,
    textAlign: 'left',
  },
  successBox: {
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.4)',
    borderRadius: 8,
    color: '#34d399',
    fontSize: 13,
    padding: '10px 14px',
    marginBottom: 16,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    textAlign: 'left',
  },
  label: {
    color: '#9ca3af',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  codeInput: {
    background: '#f8f9fb',
    border: '1px solid #e2e6ef',
    borderRadius: 8,
    color: '#111827',
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '0.3em',
    padding: '14px',
    textAlign: 'center',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    marginTop: 16,
    background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    padding: '13px',
    width: '100%',
    opacity: 1,
  },
  footerText: {
    color: '#8b8fa8',
    fontSize: 13,
    marginTop: 24,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#6c63ff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    padding: 0,
  },
};
